import axios from 'axios';
import { BACKEND_URL } from './backendUrl';

export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    if (status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } else if (status === 402) {
      // Assinatura bloqueada (suspensa/expirada) — redirecionar para paywall
      const path = window.location.pathname || '';
      if (!path.startsWith('/paywall') && !path.startsWith('/login')) {
        window.location.replace('/paywall');
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
};

export const productsAPI = {
  getAll: (category) => api.get('/products', { params: { category } }),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  getCategories: () => api.get('/categories'),
  importExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/products/import-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importCsv: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/products/import-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getImportTemplate: () => api.get('/products/export-template'),
  importPreview: (file, forceHeader) => {
    const formData = new FormData();
    formData.append('file', file);
    if (forceHeader === true || forceHeader === false) {
      formData.append('force_header', forceHeader ? 'true' : 'false');
    }
    return api.post('/products/import-preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importMapped: (file, mapping, hasHeader, tipoVendaPadrao) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mapping', JSON.stringify(mapping));
    formData.append('has_header', hasHeader ? 'true' : 'false');
    formData.append('tipo_venda_padrao', tipoVendaPadrao || 'unidade');
    return api.post('/products/import-mapped', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

export const customersAPI = {
  getAll: () => api.get('/customers'),
  create: (data) => api.post('/customers', data),
  getById: (id) => api.get(`/customers/${id}`),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
};

export const salesAPI = {
  create: (data) => api.post('/sales', data),
  getAll: (limit) => api.get('/sales', { params: { limit } }),
  getById: (id) => api.get(`/sales/${id}`),
  update: (id, data) => api.put(`/sales/${id}`, data),
  delete: (id) => api.delete(`/sales/${id}`),
  cleanNonFiscal: () => api.delete('/vendas/limpar-nao-fiscais'),
  reenviarNfce: (saleId, tipoDocumento = 'nfce') => api.post(`/sales/${saleId}/reenviar-nfce`, { tipo_documento: tipoDocumento }),
  emitirNfce: (saleId) => api.post(`/sales/${saleId}/emitir-nfce`),
  emitirNfceComOpcoes: (saleId, opcoes) => api.post(`/sales/${saleId}/emitir-nfce-opcoes`, opcoes),
  emitirNota: (saleId, tipoDocumento) => api.post(`/sales/${saleId}/emitir-nota`, { tipo_documento: tipoDocumento }),
  cancelarNfce: (saleId, justificativa) => api.post(`/sales/${saleId}/cancelar-nfce`, { justificativa }),
  // Gerenciamento de duplicados
  getDuplicados: () => api.get('/vendas/duplicados'),
  limparDuplicados: () => api.post('/vendas/limpar-duplicados'),
  excluirPorNumero: (numero, manterAutorizada = true) => 
    api.delete(`/vendas/numero/${numero}`, { params: { manter_autorizada: manterAutorizada } }),
};

export const nfceAPI = {
  listXmls: (data_inicio, data_fim) => api.get('/nfce/xmls', { params: { data_inicio, data_fim } }),
  getDownloadUrl: (data_inicio, data_fim) => `${API}/nfce/xmls/download?data_inicio=${data_inicio}&data_fim=${data_fim}`,
  getSingleXmlUrl: (saleId) => `${API}/nfce/xml/${saleId}`,
  inutilizar: (data) => api.post('/nfce/inutilizar', data),
  listarInutilizacoes: () => api.get('/nfce/inutilizacoes'),
};

export const cashRegisterAPI = {
  open: (opening_balance) => api.post('/cash-register/open', { opening_balance }),
  getCurrent: () => api.get('/cash-register/current'),
  close: (closing_balance) => api.post('/cash-register/close', { closing_balance }),
};

export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

export const pixAPI = {
  criar: (valor, descricao, id_externo) => api.post('/pix/criar', { valor, descricao, id_externo }),
  verificarStatus: (paymentId) => api.get(`/pix/status/${paymentId}`),
  cancelar: (paymentId) => api.post(`/pix/cancelar/${paymentId}`),
};

export const paymentSettingsAPI = {
  get: () => api.get('/settings/pagamento'),
  update: (data) => api.post('/settings/pagamento', data),
};

export const paymentMethodsAPI = {
  list: (apenasAtivos = false) => api.get('/payment-methods', { params: { apenas_ativos: apenasAtivos } }),
  create: (data) => api.post('/payment-methods', data),
  update: (id, data) => api.put(`/payment-methods/${id}`, data),
  remove: (id) => api.delete(`/payment-methods/${id}`),
};

export const balancaAPI = {
  get: () => api.get('/settings/balanca'),
  save: (data) => api.post('/settings/balanca', data),
};

export const ordersAPI = {
  getWhatsAppOrders: (status) => api.get('/whatsapp/orders', { params: { status } }),
  updateWhatsAppOrderStatus: (orderId, status) => api.put(`/whatsapp/orders/${orderId}/status`, null, { params: { status } }),
  generateCashFlowFromOrder: (orderId) => api.post(`/whatsapp/orders/${orderId}/generate-cashflow`),
};
